import './Storyboard.css';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Divider, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import StoryboardPromptGen from './StoryboardPromptGen/StoryboardPromptGen';
import ReviewStoryboard from './ReviewStoryboard/ReviewStoryboard';
import { fetchRequest } from '../../../utils/Fetcher';
import { selectSessionId } from '../../../store/sessionSlice';
import { SaveProgress } from '../../../utils/SaveProgress';
import { runStoryboardJob } from '../../../utils/polls/RunStoryboardJob';
import { runSceneGenerationJob } from '../../../utils/polls/RunSceneGenerationJob';

const Storyboard = ({
  selectedPersonaId,
  selectedConceptIds,
  scenes,
  setScenes,
  storyboardId,
  setStoryboardId,
  descriptionPrompt,
  setDescriptionPrompt,
  onNext,
}) => {
  const desc = [
    'Configure storyboard based on generated description',
    'Review storyboard scene details',
  ];

  const [flowIndex, setFlowIndex] = useState(0);
  const [scenesCount, setScenesCount] = useState(3);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState('');
  const [error, setError] = useState('');

  const sessionId = useSelector(selectSessionId);

  const conceptKey = useMemo(
    () => (selectedConceptIds?.length ? selectedConceptIds.join(',') : ''),
    [selectedConceptIds]
  );

  const normalizeScenes = (payload) => {
    const list =
      payload?.data?.items ??
      payload?.data ??
      payload?.items ??
      payload;

    return Array.isArray(list) ? list : [];
  };

  const normalizeStoryboardList = (payload) => {
    const list =
      payload?.data?.items ??
      payload?.data ??
      payload?.items ??
      payload;

    return Array.isArray(list) ? list : [];
  };

  const fetchPromptData = async () => {
    setLoadingDesc('AI is generating storyboard description for you...');

    const { result } = await fetchRequest({
      method: 'POST',
      path: 'sessions/storyboards/prompts/generate-input',
      data: JSON.stringify({
        customerPersonaId: selectedPersonaId,
        campaignConceptId: selectedConceptIds,
        numOfScene: 1,
      }),
    });

    return result;
  };

  const fetchScenes = async (id, limitOverride) => {
    setLoadingDesc('Fetching scenes in storyboard...');

    const qs = new URLSearchParams({
      limit: String(limitOverride ?? scenesCount),
    }).toString();

    const { result } = await fetchRequest({
      method: 'GET',
      path: `sessions/storyboards/${id}/scenes?${qs}`,
    });

    return normalizeScenes(result);
  };

  const fetchExistingStoryboards = async () => {
    setLoadingDesc('Fetching storyboard...');

    const qs = new URLSearchParams({ limit: 10 }).toString();

    const { result } = await fetchRequest({
      method: 'GET',
      path: `sessions/storyboards?${qs}`,
    });

    return normalizeStoryboardList(result);
  };

  const generateStoryboard = async () => {
    setLoadingDesc('AI is generating your storyboard...');

    return runStoryboardJob({
      body: {
        campaignConceptId: selectedConceptIds,
        prompt: descriptionPrompt,
        applyBrandGuideline: true,
      },
    });
  };

  const goNext = async () => {
    if (flowIndex === 1) {
      onNext();
      return;
    }

    try {
      setGenerating(true);
      setError('');
      setScenes([]);
      setStoryboardId(null);

      setFlowIndex(1);

      const storyboard = await generateStoryboard();
      const newStoryboardId = storyboard?.id;

      if (!newStoryboardId) {
        throw new Error('Storyboard ID not found');
      }

      setStoryboardId(newStoryboardId);

      await SaveProgress({
        sessionId,
        method: 'POST',
        param: 'storyboards/prompts/save',
        payload: {
          selectedIds: [newStoryboardId],
        },
      });

      const fetchedScenes = await runSceneGenerationJob({
        storyboardId: newStoryboardId,
        numberOfScene: scenesCount,
        applyBrandGuideline: true,
        onProgress: setLoadingDesc,
      });

      // await runSceneGenerationJob({
      //   storyboardId: newStoryboardId,
      //   numberOfScene: scenesCount,
      //   applyBrandGuideline: true,
      //   onProgress: setLoadingDesc,
      // });

      // const fetchedScenes = await fetchScenes(newStoryboardId, scenesCount);
      setScenes(fetchedScenes);
    } catch (e) {
      setError(e.message || String(e));
      setFlowIndex(0);
    } finally {
      setGenerating(false);
      setLoadingDesc('');
    }
  };

  const goBack = () => {
    if (generating) return;
    setError('');
    setFlowIndex(0);
  };

  useEffect(() => {
    if (!sessionId) return;
    if (!selectedPersonaId || !selectedConceptIds?.length) return;

    let cancelled = false;

    const bootstrap = async () => {
      try {
        setBootstrapping(true);
        setError('');
        setLoadingDesc('');
        setScenes([]);
        setStoryboardId(null);

        const storyboards = await fetchExistingStoryboards();
        if (cancelled) return;

        if (storyboards.length > 0) {
          const latest = storyboards
            .slice()
            .sort(
              (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
            )[0];

          const existingId = latest?.id;

          if (existingId) {
            const n = latest?.numScenes ?? scenesCount;

            setStoryboardId(existingId);
            setScenesCount(n);
            setFlowIndex(1);

            const existingScenes = await fetchScenes(existingId, n);
            if (cancelled) return;

            setScenes(existingScenes);
            return;
          }
        }

        setFlowIndex(0);
        setDescriptionPrompt('');

        const data = await fetchPromptData();
        if (cancelled) return;

        setDescriptionPrompt(data?.[0]?.description ?? '');
      } catch (e) {
        if (!cancelled) {
          setError(e.message || String(e));
          setFlowIndex(0);
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
          setLoadingDesc('');
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [sessionId, selectedPersonaId, conceptKey]);

  const promptLoading = bootstrapping && flowIndex === 0;
  const reviewLoading = bootstrapping || generating;

  return (
    <div className="image-generation-container">
      <div className="content-title-container">
        <div className="content-title">
          {desc[flowIndex]}
          <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_STORYBOARD}>
              <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
          </Tooltip>
        </div>
        <Divider className="content-title-divider" />
      </div>

      {flowIndex === 0 && (
        <StoryboardPromptGen
          descriptionPrompt={descriptionPrompt}
          onDescriptionChange={setDescriptionPrompt}
          loading={promptLoading}
          error={error}
          scenesCount={scenesCount}
          onScenesCountChange={(val) => setScenesCount(val)}
        />
      )}

      {flowIndex === 1 && (
        <ReviewStoryboard
          scenes={scenes}
          setScenes={setScenes}
          loading={reviewLoading}
          loadingDesc={loadingDesc}
          error={error}
          storyboardId={storyboardId}
        />
      )}

      <div className="back-next-container">
        <div className="back-slot">
          {flowIndex !== 0 && (
            <Button
              onClick={goBack}
              className="next-button"
              disabled={generating}
            >
              Back
            </Button>
          )}
        </div>

        <Button
          onClick={goNext}
          className="next-button"
          disabled={bootstrapping || generating}
          loading={promptLoading}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Storyboard;